import { pendingBonus, takePendingBonus } from "../../dice/pending";
import { parse, type Roll } from "../../dice/pool";
import { heldDiceStates } from "../../dice/preferences";
import { type Outcome, rollAndPost, type Subject } from "../../dice/roll_and_post";
import type { RollSpec, Roller } from "../ui/roll_chip";
import type { Dict } from "./labels";
import { adjusted } from "./rolls";

// `masked`: the roll was posted as a monster whose name the campaign's feed hides.
export type Rolled = { outcome: Outcome; description: string; masked?: boolean };

// The campaign's rolls URL for this viewer: only one who may edit the sheet posts, and only for a campaign's sheet.
export function rollUrl(template: string | undefined, meta: { can_update: boolean; campaign_id: string | null } | undefined): string | null {
	return template && meta?.can_update && meta.campaign_id ? template.replace("__campaign__", encodeURIComponent(meta.campaign_id)) : null;
}

// The dice bar's pending bonus is spent by a roll that parses; secret and silent are the bar's toggles, read at each roll.
export function createRoller<Captured = undefined>({
	url,
	subject,
	names,
	title,
	capture,
	show,
	rolling,
}: {
	url: () => string | null;
	subject: () => Subject | undefined;
	names: Dict;
	title: (spec: RollSpec) => string;
	// Read as a roll starts and handed to `show` with its result, however late the answer, so the roll keeps the attribution it began with.
	capture?: () => Captured;
	show: (rolled: Rolled, captured: Captured) => void;
	// Called as a roll that will be posted starts, before the server answers.
	rolling?: (roll: Roll, description: string) => void;
}): Roller {
	return {
		title,
		async roll(spec) {
			const captured = capture?.() as Captured;
			const used = adjusted(spec, pendingBonus(), names);
			if (!parse(used.formula).ok) return null;
			takePendingBonus();
			const states = heldDiceStates();
			const who = subject();
			const outcome = await rollAndPost({
				formula: used.formula,
				url: url(),
				secret: Boolean(states.hide),
				success: used.success,
				attack: used.attack,
				description: used.description,
				subject: who,
				onRolling: rolling && ((started) => rolling(started, used.description)),
			});
			if (!outcome) return null;
			show(
				{
					outcome,
					description: used.description,
					masked: (outcome.kind === "failed" ? undefined : outcome.masked) ?? (!!who && "masked" in who && who.masked),
				},
				captured,
			);
			return outcome.roll;
		},
	};
}
